// -----------------------------------------------------------------------------
//  THE OUTBOX — what the phone does when there is no signal
// -----------------------------------------------------------------------------
//
//  Kouk Thlok has no mobile data. Every screen written so far calls the server
//  the moment the officer taps Save, which means that in the village every
//  screen fails. The outbox is the answer: a tap Save writes the record to the
//  phone and puts a description of the operation in a queue. Later, wherever
//  there is signal, the queue is replayed.
//
//  THE ONE RULE THIS FILE EXISTS TO ENFORCE
//  ----------------------------------------
//  An entry is deleted only when the SERVER says it was applied. Never because
//  it looked like it worked, never because it failed, never to tidy up, never
//  on sign-out. A queue entry is a family's data, collected on foot, in the
//  heat, once. Losing it is not a bug that can be fixed afterwards, because
//  nobody knows what was lost.
//
//  WHY THE LOGIC LIVES HERE AND NOT IN THE SCREENS
//  -----------------------------------------------
//  This module talks to an injected store and an injected rpc function, and
//  touches no browser API at all. That is not architecture for its own sake:
//  it means the failure cases — signal dying mid-upload, an expired token, a
//  rejected record, the app closed halfway — can be tested in Node, on a
//  laptop, in a second, instead of by walking to a village and hoping.
// -----------------------------------------------------------------------------

export type OutboxKind = 'create_household' | 'assessment' | 'correct_details'

/** The remote procedure each kind of entry replays into. */
export const RPC_FOR: Record<OutboxKind, string> = {
  create_household: 'create_household_safe',
  assessment: 'record_household_assessment_safe',
  correct_details: 'update_household_details_safe',
}

export type OutboxEntry = {
  /** Auto-assigned by the store. Ordering is queue order, oldest first. */
  seq?: number
  /**
   * Generated on the device when the officer taps Save, and never regenerated.
   * This is the entire basis of retry safety: the server recognises a repeat
   * and returns the original answer instead of doing the work twice.
   */
  op_id: string
  kind: OutboxKind
  /** What the entry is about, so the UI can say "Sok Chan's house is waiting". */
  household_uid: string
  /** The exact named arguments for the remote procedure. Nothing computed later. */
  payload: Record<string, unknown>
  created_at: string
  attempts: number
  last_error: string | null
  /**
   *  pending    not yet accepted; will be tried again
   *  rejected   the server refused it for a reason retrying cannot fix.
   *             STILL IN THE QUEUE. A person has to look at it.
   */
  status: 'pending' | 'rejected'
}

export interface OutboxStore {
  /** Oldest first. */
  list(): Promise<OutboxEntry[]>
  add(entry: Omit<OutboxEntry, 'seq'>): Promise<void>
  update(op_id: string, patch: Partial<OutboxEntry>): Promise<void>
  remove(op_id: string): Promise<void>
}

export type RpcError = { message: string; code?: string }
export type RpcFn = (fn: string, args: Record<string, unknown>) => Promise<{ error: RpcError | null }>

export type SyncResult = {
  sent: number
  rejected: number
  remaining: number
  /** Why the run stopped early, if it did. Null means the queue was worked through. */
  stopped: null | 'offline' | 'signed_out'
  errors: string[]
}

// -----------------------------------------------------------------------------
//  CLASSIFYING A FAILURE
// -----------------------------------------------------------------------------
//  Getting this wrong is how offline apps lose data. Three outcomes, and they
//  could not be more different:
//
//    transport  the phone could not reach the server. Nothing is known about
//               whether the operation applied. Keep the entry, stop the run —
//               every later entry would fail the same way, and retrying in a
//               loop only flattens the battery.
//
//    auth       the token expired while the officer was walking. Refreshing
//               needs signal; if that fails, stop and say "sign in again".
//               NOT the entry's fault; it must not be marked rejected.
//
//    rejected   the server understood and said no: a broken constraint, a
//               village the officer is not assigned to. Retrying forever will
//               never help. Mark it, keep it, and move on to the next entry
//               so one bad record cannot block the whole queue.
// -----------------------------------------------------------------------------

const TRANSPORT = [
  'failed to fetch',
  'networkerror',
  'network request failed',
  'load failed',            // Safari's phrasing for the same thing
  'err_internet_disconnected',
  'timeout',
  'aborted',
  'service unavailable',
  'gateway',
  'fetch failed',
]

const AUTH = [
  'jwt expired',
  'jwt issued at future',
  'invalid claim',
  'invalid jwt',
  'no api key',
  'refresh_token',
  'not signed in',
]

export function classify(err: RpcError): 'transport' | 'auth' | 'rejected' {
  const m = (err.message || '').toLowerCase()
  if (TRANSPORT.some((t) => m.includes(t))) return 'transport'
  if (AUTH.some((t) => m.includes(t))) return 'auth'
  // A 5xx is the server being unwell, not the record being wrong.
  if (/\b5\d\d\b/.test(m)) return 'transport'
  return 'rejected'
}

// -----------------------------------------------------------------------------
//  ONE PASS OVER THE QUEUE
// -----------------------------------------------------------------------------

export async function syncOnce(
  store: OutboxStore,
  rpc: RpcFn,
  opts?: {
    /** Called once when a token looks expired. Return true if a fresh one was obtained. */
    refreshSession?: () => Promise<boolean>
    /** Retry entries already marked rejected — for the "Try again" button. */
    includeRejected?: boolean
    onProgress?: (done: number, total: number) => void
  },
): Promise<SyncResult> {
  const all = await store.list()
  const queue = opts?.includeRejected ? all : all.filter((e) => e.status === 'pending')

  const result: SyncResult = { sent: 0, rejected: 0, remaining: 0, stopped: null, errors: [] }
  let refreshed = false

  // If a house is refused, its assessment cannot possibly land. Skipping the
  // dependents keeps one real problem from printing five confusing ones.
  const blocked = new Set<string>()

  for (let i = 0; i < queue.length; i++) {
    const entry = queue[i]
    opts?.onProgress?.(i, queue.length)

    if (blocked.has(entry.household_uid)) {
      await store.update(entry.op_id, {
        status: 'rejected',
        last_error: 'Waiting on the household record above, which the server refused.',
      })
      result.rejected++
      continue
    }

    let { error } = await rpc(RPC_FOR[entry.kind], entry.payload)

    // One refresh attempt per run, then retry this same entry once.
    if (error && classify(error) === 'auth' && !refreshed && opts?.refreshSession) {
      refreshed = true
      if (await opts.refreshSession()) {
        ;({ error } = await rpc(RPC_FOR[entry.kind], entry.payload))
      }
    }

    if (!error) {
      // The ONLY place an entry is ever removed.
      await store.remove(entry.op_id)
      result.sent++
      continue
    }

    const kind = classify(error)
    await store.update(entry.op_id, {
      attempts: entry.attempts + 1,
      last_error: error.message,
      // transport and auth leave it pending: nothing is wrong with the record.
      status: kind === 'rejected' ? 'rejected' : 'pending',
    })

    if (kind === 'transport' || kind === 'auth') {
      result.stopped = kind === 'auth' ? 'signed_out' : 'offline'
      result.errors.push(error.message)
      break
    }

    result.rejected++
    result.errors.push(`${entry.kind}: ${error.message}`)
    if (entry.kind === 'create_household') blocked.add(entry.household_uid)
  }

  result.remaining = (await store.list()).length
  opts?.onProgress?.(queue.length, queue.length)
  return result
}

// -----------------------------------------------------------------------------
//  ONE SYNC AT A TIME
// -----------------------------------------------------------------------------
//  Sync is triggered from several places — the browser coming online, a timer,
//  the officer tapping the banner, the app being brought back to the
//  foreground. Two runs at once would send the same entry twice. The server
//  would cope, because of op_id, but the counts shown to the officer would be
//  nonsense and nonsense counts are how people learn to distrust a screen.
// -----------------------------------------------------------------------------

let inFlight: Promise<SyncResult> | null = null

export function syncNow(
  store: OutboxStore,
  rpc: RpcFn,
  opts?: Parameters<typeof syncOnce>[2],
): Promise<SyncResult> {
  if (inFlight) return inFlight
  inFlight = syncOnce(store, rpc, opts).finally(() => {
    inFlight = null
  })
  return inFlight
}

/** Exposed for tests only. */
export function _resetInFlight() {
  inFlight = null
}

// -----------------------------------------------------------------------------
//  BUILDING THE ENTRIES
// -----------------------------------------------------------------------------
//  Each of these produces a complete, self-contained instruction. Migration 011
//  is what makes that possible for a household: the phone chooses the
//  household_uid, so an assessment can name the house before the server has
//  ever heard of it.
// -----------------------------------------------------------------------------

export function newOpId(): string {
  return crypto.randomUUID()
}

export function queueCreateHousehold(args: {
  household_uid: string
  village_id: string
  head_name: string | null
  head_sex: string | null
  counts: Record<string, number>
  lat: number | null
  lon: number | null
  gps_accuracy_m: number | null
  gps_provider: string | null
  gps_captured_at: string | null
  device: string
}): Omit<OutboxEntry, 'seq'> {
  // ONE id, used as both the queue key and the server's idempotency key. Two
  // different values here would mean a retry the queue thinks is the same
  // entry but the server thinks is new work — the exact bug op_id exists to
  // prevent, hidden one level down.
  const op_id = newOpId()
  return {
    op_id,
    kind: 'create_household',
    household_uid: args.household_uid,
    payload: {
      p_op_id: op_id,
      p_household_uid: args.household_uid,
      p_village_id: args.village_id,
      p_head_name: args.head_name,
      p_head_sex: args.head_sex,
      p_adults_male: args.counts.adults_male_18_59 ?? 0,
      p_adults_female: args.counts.adults_female_18_59 ?? 0,
      p_children: args.counts.children_under_18 ?? 0,
      p_elderly: args.counts.elderly_60_plus ?? 0,
      p_disability: args.counts.members_with_disability ?? 0,
      p_lat: args.lat,
      p_lon: args.lon,
      p_gps_accuracy_m: args.gps_accuracy_m,
      p_gps_provider: args.gps_provider,
      p_gps_captured_at: args.gps_captured_at,
      p_record_status: 'active',
      p_device: args.device,
    },
    created_at: new Date().toISOString(),
    attempts: 0,
    last_error: null,
    status: 'pending',
  }
}

export function queueAssessment(args: {
  household_uid: string
  values: Record<string, unknown>
  purpose: string
  observed_at: string
  lat?: number | null
  lon?: number | null
  gps_accuracy_m?: number | null
  notes?: string | null
  device: string
}): Omit<OutboxEntry, 'seq'> {
  const op_id = newOpId()
  return {
    op_id,
    kind: 'assessment',
    household_uid: args.household_uid,
    payload: {
      p_op_id: op_id,
      p_household_uid: args.household_uid,
      p_values: args.values,
      p_visit_purpose: args.purpose,
      p_visit_outcome: 'completed',
      // The date the officer STOOD THERE, not the date it happened to upload.
      // Getting this wrong quietly moves every offline observation to whenever
      // the phone next found signal, which corrupts every time series drawn
      // from it.
      p_observed_at: args.observed_at,
      p_lat: args.lat ?? null,
      p_lon: args.lon ?? null,
      p_gps_accuracy_m: args.gps_accuracy_m ?? null,
      p_notes: args.notes ?? null,
      p_device: args.device,
    },
    created_at: new Date().toISOString(),
    attempts: 0,
    last_error: null,
    status: 'pending',
  }
}

export function queueCorrection(args: {
  household_uid: string
  head_name?: string | null
  head_sex?: string | null
  record_status?: string | null
  status_note?: string | null
  lat?: number | null
  lon?: number | null
  gps_accuracy_m?: number | null
  gps_provider?: string | null
  device: string
}): Omit<OutboxEntry, 'seq'> {
  const op_id = newOpId()
  return {
    op_id,
    kind: 'correct_details',
    household_uid: args.household_uid,
    payload: {
      p_op_id: op_id,
      p_household_uid: args.household_uid,
      p_head_name: args.head_name ?? null,
      p_head_sex: args.head_sex ?? null,
      p_record_status: args.record_status ?? null,
      p_status_note: args.status_note ?? null,
      p_lat: args.lat ?? null,
      p_lon: args.lon ?? null,
      p_gps_accuracy_m: args.gps_accuracy_m ?? null,
      p_gps_provider: args.gps_provider ?? null,
      p_device: args.device,
    },
    created_at: new Date().toISOString(),
    attempts: 0,
    last_error: null,
    status: 'pending',
  }
}
